import type { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import type {
  DragEndEvent as ReactDragEndEvent,
  DragOverEvent as ReactDragOverEvent,
} from '@dnd-kit/react';
import { v7 as uuidv7 } from 'uuid';

import { cleanupParentContainers } from '@/lib/blocks/cleanup';
import { isCollectionBlock, resolveCollectionLayout } from '@/lib/blocks/collection/blocks';
import {
  BLOCK_STACK_DIRECTION_DEFAULT,
  type BlockStackLayoutConfig,
} from '@/lib/blocks/layout/block-stack-layout';
import {
  FLOAT_RESTORE_POSITION_PROP,
  GROUP_ALIGN_DEFAULT,
  GROUP_GAP_DEFAULT,
  GROUP_JUSTIFY_DEFAULT,
  type GroupAlign,
  type GroupDirection,
  type GroupGap,
  type GroupJustify,
  isBlockFloated,
  resolveGroupLayout,
  resolveGroupLayoutSettings,
} from '@/lib/blocks/layout/group-layout';
import { normalizeBlocksStructure } from '@/lib/blocks/normalization';
import { COLUMN_SPAN_DEFAULT, clampColumnSpan } from '@/lib/blocks/tree';
import { readRecord } from '@/lib/prop-utils';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import {
  getDropIntentDirectionFromDelta,
  getIntentDirectionFromEdge,
} from './decision/drag-intent';
import { isCollectionScopedDrag } from './policies';
import {
  buildDropResolutionContext,
  type DropResolutionContext,
} from './resolution/context-builder';
import {
  type DropValidationEventInput,
  type EventOverSnapshot,
  getEventActivator,
  getEventDelta,
} from './resolution/event-adapters';
import { hasValidTargetHierarchy } from './resolution/hierarchy-rules';
import { resolveCanonicalDropIndicator } from './resolution/indicator-resolution';
import {
  canMoveBlockToTargetParent,
  getSameParentReorderInsertionIndex,
  moveBlockToTargetParent,
  reorderWithinSameParentIfNeeded,
} from './resolution/reorder-move-ops';

// This module owns drag/drop decision logic for block trees:
// - choose droppable targets (collision strategy)
// - resolve drop intent into tree operations (reorder/reparent/group)
// - normalize final structure after each drop

/** Minimum pointer movement before starting a drag and PointerSensor activation. */
export const DRAG_START_DISTANCE_PX = 12;

// Drag must move at least this much on one axis before we treat it as strong intent.
// 48px is deliberate: large enough to avoid accidental grouping while still feeling responsive.
const GROUP_INTENT_THRESHOLD_PX = 48;
const CONTENT_GROUP_DEFAULT_COLUMN_SPAN = clampColumnSpan(Math.floor(COLUMN_SPAN_DEFAULT / 2));

export type IndicatorEdge = 'top' | 'bottom' | 'left' | 'right' | 'end';
export type DirectionalIndicatorEdge = Exclude<IndicatorEdge, 'end'>;
export type DropParentDirection = GroupDirection | 'grid';

export interface DropIndicator {
  targetId: string;
  edge: IndicatorEdge;
}

export type BlockDragData = {
  // Attached to sortable block nodes so we always know source parent while dragging.
  type: 'block';
  parentId: string | null;
  // Parent wrappers (groups) use different collision preference than leaf blocks.
  isParentBlock?: boolean;
};

export type ActiveDragData = BlockDragData;

export type ParentDropData = {
  // Attached to hidden "append at end of parent" targets.
  type: 'parent-drop';
  parentId: string | null;
};

export type GapZoneData = {
  // Explicit edge target around a block. This is the primary source of insert intent.
  type: 'insert-gap-zone' | 'group-gap-zone';
  blockId: string;
  edge: DirectionalIndicatorEdge;
  parentId: string | null;
  // Layout direction of the parent container that owns this gap zone.
  parentDirection?: DropParentDirection;
};

export const isDirectionalEdge = (
  edge: IndicatorEdge | null | undefined,
): edge is DirectionalIndicatorEdge =>
  edge === 'top' || edge === 'bottom' || edge === 'left' || edge === 'right';

export const isMouseActivatorEvent = (event: unknown): event is MouseEvent =>
  typeof MouseEvent !== 'undefined' && event instanceof MouseEvent;

export const isTouchActivatorEvent = (event: unknown): event is TouchEvent =>
  typeof TouchEvent !== 'undefined' && event instanceof TouchEvent;

const isPointerActivatorEvent = (event: unknown): event is PointerEvent =>
  typeof PointerEvent !== 'undefined' && event instanceof PointerEvent;

export const isPointerDrivenActivator = (
  event: unknown,
): event is MouseEvent | TouchEvent | PointerEvent =>
  isMouseActivatorEvent(event) || isTouchActivatorEvent(event) || isPointerActivatorEvent(event);

export const ROOT_PARENT_DROP_ID = 'parent-drop-root';

export const isParentDropData = (data: unknown): data is ParentDropData => {
  const record = readRecord(data);
  return record?.type === 'parent-drop';
};

export const isGapZoneData = (data: unknown): data is GapZoneData => {
  const record = readRecord(data);
  const type = record?.type;
  return type === 'insert-gap-zone' || type === 'group-gap-zone';
};

export const isGroupZoneData = (
  data: unknown,
): data is GapZoneData & { type: 'group-gap-zone' } => {
  return isGapZoneData(data) && data.type === 'group-gap-zone';
};

const getDirectChildren = (block: PageBlock, blocks: PageBlock[]): PageBlock[] =>
  blocks.filter((child) => (child.parentBlockId ?? null) === block.id);

const getParentDirection = (
  block: PageBlock | undefined,
  blocks: PageBlock[],
  incomingBlock?: PageBlock,
): DropParentDirection | null => {
  if (!block) {
    return null;
  }

  if (isCollectionBlock(block)) {
    const layout = resolveCollectionLayout(block);
    const isSingleColumn =
      layout.columns.mobile === 1 && layout.columns.tablet === 1 && layout.columns.desktop === 1;
    return isSingleColumn ? 'vertical' : 'grid';
  }

  if (block.blockType !== 'group') {
    return null;
  }

  const children = getDirectChildren(block, blocks);
  if (incomingBlock && incomingBlock.parentBlockId !== block.id && isBlockFloated(incomingBlock)) {
    children.push({ ...incomingBlock, parentBlockId: block.id });
  }
  const layout = resolveGroupLayout(block, children);
  return layout.isFloatLayout ? 'vertical' : layout.direction;
};

const getDragIntentDirection = (event: DropValidationEventInput): GroupDirection | null => {
  // Delta inference is reserved for non-pointer fallbacks (keyboard/programmatic drags).
  // Pointer drags should derive intent from explicit gap-zone metadata only.
  const activator = getEventActivator(event);
  const isPointerActivator = isPointerDrivenActivator(activator);
  if (isPointerActivator) {
    return null;
  }

  // Last-resort fallback only.
  const delta = getEventDelta(event);
  return getDropIntentDirectionFromDelta({
    deltaX: delta.x,
    deltaY: delta.y,
    thresholdPx: GROUP_INTENT_THRESHOLD_PX,
  });
};

const resolveMovedBlockColumnSpan = ({
  activeBlock,
  targetParent,
  overBlock,
  indicatorEdge,
  rootParentDirection,
  blocks,
}: {
  activeBlock: PageBlock;
  targetParent: PageBlock | undefined;
  overBlock: PageBlock | undefined;
  indicatorEdge: IndicatorEdge | null;
  rootParentDirection: GroupDirection;
  blocks: PageBlock[];
}): number => {
  // Keep span behavior predictable when moving between different parent layouts.
  // - vertical parents: full width
  // - horizontal parents: keep compact content-like spans
  const activeSpan = clampColumnSpan(activeBlock.columnSpan);
  const targetDirection = !targetParent
    ? rootParentDirection
    : getParentDirection(targetParent, blocks, activeBlock);

  if (
    targetParent?.blockType === 'group' &&
    targetDirection === 'vertical' &&
    isBlockFloated(activeBlock)
  ) {
    return activeSpan;
  }

  if (targetDirection === 'grid') {
    return COLUMN_SPAN_DEFAULT;
  }

  if (targetDirection === null) {
    return activeSpan;
  }

  if (targetDirection === 'vertical') {
    return COLUMN_SPAN_DEFAULT;
  }

  const overSpan = overBlock ? clampColumnSpan(overBlock.columnSpan) : null;
  const lateralDrop = indicatorEdge === 'left' || indicatorEdge === 'right';
  if (lateralDrop && overSpan && overSpan < COLUMN_SPAN_DEFAULT) {
    return overSpan;
  }

  // Imported full-width blocks should become compact inside horizontal groups.
  return activeSpan === COLUMN_SPAN_DEFAULT ? CONTENT_GROUP_DEFAULT_COLUMN_SPAN : activeSpan;
};

export function normalizeBlocks(
  blocks: PageBlock[],
  rootParentDirection: GroupDirection = BLOCK_STACK_DIRECTION_DEFAULT,
): PageBlock[] {
  return normalizeBlocksStructure(blocks, rootParentDirection);
}

export type { DropValidationEventInput, EventOverSnapshot };
export { resolveCanonicalDropIndicator };

export {
  getEventActivator,
  getEventActiveData,
  getEventActiveId,
  getEventDelta,
  getEventOver,
} from './resolution/event-adapters';

const getStableParentId = (
  block: PageBlock,
  stableIdByPersistedId: Map<string, string>,
): string | null => {
  if (!block.parentBlockId) {
    return null;
  }

  return stableIdByPersistedId.get(block.parentBlockId) ?? null;
};

const mapAlignToJustify = (align: GroupAlign | undefined): GroupJustify => {
  if (align === 'center' || align === 'end') {
    return align;
  }

  return GROUP_JUSTIFY_DEFAULT;
};

// Auto-created groups inherit only layout settings that remain semantically compatible.
// We always carry forward spacing, and when a vertical container spawns a horizontal
// subgroup we also preserve horizontal placement so centered inline content stays centered.
const resolveAutoGroupProps = ({
  intentDirection,
  targetParent,
  rootParentDirection,
  rootStackLayout,
}: {
  intentDirection: GroupDirection;
  targetParent: PageBlock | undefined;
  rootParentDirection: GroupDirection;
  rootStackLayout?: BlockStackLayoutConfig;
}) => {
  let gap: GroupGap = GROUP_GAP_DEFAULT;
  let justify: GroupJustify = GROUP_JUSTIFY_DEFAULT;

  if (targetParent?.blockType === 'group') {
    // Reuse the enclosing group's spacing, and preserve horizontal placement when a
    // vertical group spawns a horizontal subgroup (for example centered button rows).
    const targetLayout = resolveGroupLayoutSettings(targetParent);
    gap = targetLayout.gap;

    if (intentDirection === 'horizontal' && targetLayout.direction === 'vertical') {
      justify = mapAlignToJustify(targetLayout.align);
    }
  } else if (!targetParent) {
    // Root-level auto-groups inherit compatible section stack settings only.
    if (intentDirection === 'horizontal' && rootParentDirection === 'vertical') {
      justify = mapAlignToJustify(rootStackLayout?.align);
    }
  }

  return {
    direction: intentDirection,
    gap,
    align: GROUP_ALIGN_DEFAULT,
    justify,
  };
};

const canCreateDirectionalGroup = (context: DropResolutionContext): boolean => {
  const {
    activeId,
    activeParent,
    targetParent,
    targetParentDirection,
    overData,
    overBlock,
    effectiveOverId,
    intentDirection,
  } = context;

  if (isCollectionScopedDrag({ activeParent, targetParent })) {
    return false;
  }
  if (!isGroupZoneData(overData)) {
    return false;
  }
  if (!intentDirection) {
    return false;
  }

  const droppingOnBlock = !isParentDropData(overData);
  const differentTarget = activeId !== effectiveOverId;
  const canAutoGroupInParent = targetParentDirection !== 'grid';

  const result = Boolean(droppingOnBlock && differentTarget && overBlock && canAutoGroupInParent);

  return result;
};

const createDirectionalGroupIfNeeded = ({
  context,
  blocks,
}: {
  context: DropResolutionContext;
  blocks: PageBlock[];
}): PageBlock[] | null => {
  const {
    activeBlock,
    activeParentBlockId,
    activeParentId,
    targetParentBlockId,
    targetParentId,
    targetParent,
    targetParentDirection,
    rootParentDirection,
    rootStackLayout,
    intentDirection,
    overBlock,
    indicatorEdge,
  } = context;
  if (!canCreateDirectionalGroup(context) || !overBlock || !intentDirection) {
    return null;
  }

  // Create a new subgroup around dragged block + target block.
  // Edge controls their order inside the new subgroup.
  const groupId = uuidv7();
  const groupColumnSpan =
    targetParentDirection === 'horizontal'
      ? clampColumnSpan(overBlock.columnSpan)
      : COLUMN_SPAN_DEFAULT;
  const groupPosition =
    activeParentId === targetParentId
      ? Math.min(activeBlock.position, overBlock.position)
      : overBlock.position;

  const groupBlock: PageBlock = {
    id: groupId,
    uiKey: groupId,
    blockType: 'group',
    props: resolveAutoGroupProps({
      intentDirection,
      targetParent,
      rootParentDirection,
      rootStackLayout,
    }),
    parentBlockId: targetParentBlockId,
    position: groupPosition,
    columnSpan: groupColumnSpan,
    visible: true,
  };

  // A block that's already floated must land first in the new group regardless of which edge
  // formed it — CSS float can only affect content that comes after it in document order, so a
  // right/bottom-edge drop (which would otherwise place it second) would silently break its wrap.
  const activeIsFloated = isBlockFloated(activeBlock);
  const overIsFloated = isBlockFloated(overBlock);
  const activeFirst = activeIsFloated
    ? true
    : overIsFloated
      ? false
      : indicatorEdge === 'top' || indicatorEdge === 'left';

  const updated = blocks.map((block) => {
    if (block.id === activeBlock.id || block.id === overBlock.id) {
      const position = block.id === activeBlock.id ? (activeFirst ? 1 : 2) : activeFirst ? 2 : 1;
      const props = { ...(block.props ?? {}) };
      delete props[FLOAT_RESTORE_POSITION_PROP];

      return {
        ...block,
        props,
        parentBlockId: groupId,
        position,
        columnSpan: COLUMN_SPAN_DEFAULT,
      };
    }

    return block;
  });

  let updatedBlocks = [...updated, groupBlock];
  if (activeParentId !== targetParentId) {
    updatedBlocks = cleanupParentContainers(updatedBlocks, activeParentBlockId);
  }

  return updatedBlocks;
};

type OperationEdge = DirectionalIndicatorEdge;

export type DropOperation =
  | { type: 'insert'; parentId: string | null; targetId: string; edge: OperationEdge }
  | { type: 'group'; parentId: string | null; targetId: string; edge: OperationEdge }
  | { type: 'append'; parentId: string | null };

const resolveOperationEdge = (context: DropResolutionContext): OperationEdge | null => {
  const edge = context.indicatorEdge;
  if (!edge || edge === 'end') {
    return null;
  }
  return edge;
};

const resolveDropOperationForContext = ({
  context,
  blocks,
}: {
  context: DropResolutionContext;
  blocks: PageBlock[];
}): DropOperation | null => {
  const edge = resolveOperationEdge(context);
  if (canCreateDirectionalGroup(context) && edge) {
    return {
      type: 'group',
      parentId: context.targetParentId,
      targetId: context.effectiveOverId,
      edge,
    };
  }

  const reorderedResult = getSameParentReorderInsertionIndex({
    context,
    blocks,
    isParentDrop: isParentDropData,
  });
  if (reorderedResult && reorderedResult.oldIndex !== reorderedResult.insertionIndex && edge) {
    return {
      type: 'insert',
      parentId: context.targetParentId,
      targetId: context.effectiveOverId,
      edge,
    };
  }

  const canMove = canMoveBlockToTargetParent({
    context,
    blocks,
    isParentDrop: isParentDropData,
    resolveMovedBlockColumnSpan,
  });
  if (!canMove) {
    return null;
  }

  if (isParentDropData(context.overData)) {
    return { type: 'append', parentId: context.targetParentId };
  }

  if (context.overBlock && edge) {
    return {
      type: 'insert',
      parentId: context.targetParentId,
      targetId: context.effectiveOverId,
      edge,
    };
  }

  return { type: 'append', parentId: context.targetParentId };
};

const buildOperationContext = ({
  event,
  blockMap,
  dropIndicator,
  fallbackOver,
  rootParentDirection,
  rootStackLayout,
}: {
  event: DropValidationEventInput;
  blockMap: Map<string, PageBlock>;
  dropIndicator: DropIndicator | null;
  fallbackOver?: EventOverSnapshot | null;
  rootParentDirection: GroupDirection;
  rootStackLayout?: BlockStackLayoutConfig;
}): DropResolutionContext | null =>
  buildDropResolutionContext({
    event,
    blockMap,
    dropIndicator,
    fallbackOver,
    isGapZoneData,
    isParentDropData,
    getStableParentId,
    getParentDirection,
    getIntentDirectionFromEdge,
    getDragIntentDirection,
    resolveCanonicalDropIndicator,
    rootParentDirection,
    rootStackLayout,
  });

export function canResolveDropResult({
  event,
  blocks,
  blockMap,
  childrenByParent,
  dropIndicator,
  rootParentDirection,
  rootStackLayout,
  structuralRegionRootByBlockId,
  protectedBlockIds,
}: {
  event: DropValidationEventInput;
  blocks: PageBlock[];
  blockMap: Map<string, PageBlock>;
  childrenByParent: Map<string, PageBlock[]>;
  dropIndicator: DropIndicator | null;
  rootParentDirection: GroupDirection;
  rootStackLayout?: BlockStackLayoutConfig;
  structuralRegionRootByBlockId?: ReadonlyMap<string, string>;
  protectedBlockIds?: ReadonlySet<string>;
}): boolean {
  const context = buildOperationContext({
    event,
    blockMap,
    dropIndicator,
    rootParentDirection,
    rootStackLayout,
  });
  if (!context) {
    return false;
  }

  if (
    !hasValidTargetHierarchy({
      context,
      childrenByParent,
      structuralRegionRootByBlockId,
      protectedBlockIds,
    })
  ) {
    return false;
  }

  const operation = resolveDropOperationForContext({
    context,
    blocks,
  });
  return operation !== null;
}

export function getSameParentReorderPreview({
  event,
  blocks,
  blockMap,
  childrenByParent,
  dropIndicator,
  rootParentDirection,
  rootStackLayout,
  structuralRegionRootByBlockId,
  protectedBlockIds,
}: {
  event: DragOverEvent | ReactDragOverEvent;
  blocks: PageBlock[];
  blockMap: Map<string, PageBlock>;
  childrenByParent: Map<string, PageBlock[]>;
  dropIndicator: DropIndicator | null;
  rootParentDirection: GroupDirection;
  rootStackLayout?: BlockStackLayoutConfig;
  structuralRegionRootByBlockId?: ReadonlyMap<string, string>;
  protectedBlockIds?: ReadonlySet<string>;
}): { parentId: string | null; items: string[] } | null {
  const context = buildOperationContext({
    event,
    blockMap,
    dropIndicator,
    rootParentDirection,
    rootStackLayout,
  });
  if (!context) {
    return null;
  }

  if (
    !hasValidTargetHierarchy({
      context,
      childrenByParent,
      structuralRegionRootByBlockId,
      protectedBlockIds,
    })
  ) {
    return null;
  }

  const operation = resolveDropOperationForContext({ context, blocks });
  if (!operation || operation.type !== 'insert') {
    return null;
  }

  const result = getSameParentReorderInsertionIndex({
    context,
    blocks,
    isParentDrop: isParentDropData,
  });
  if (!result) {
    return null;
  }

  const { activeBlock, activeParentId } = context;
  const { insertionIndex, siblingsWithoutActive } = result;
  const reordered = [...siblingsWithoutActive];
  reordered.splice(insertionIndex, 0, activeBlock);

  return {
    parentId: activeParentId,
    items: reordered.map(getBlockUiKey),
  };
}

export function resolveDropResult({
  event,
  blocks,
  blockMap,
  childrenByParent,
  dropIndicator,
  fallbackOver,
  rootParentDirection,
  rootStackLayout,
  structuralRegionRootByBlockId,
  protectedBlockIds,
}: {
  event: DragEndEvent | ReactDragEndEvent;
  blocks: PageBlock[];
  blockMap: Map<string, PageBlock>;
  childrenByParent: Map<string, PageBlock[]>;
  dropIndicator: DropIndicator | null;
  fallbackOver?: EventOverSnapshot | null;
  rootParentDirection: GroupDirection;
  rootStackLayout?: BlockStackLayoutConfig;
  structuralRegionRootByBlockId?: ReadonlyMap<string, string>;
  protectedBlockIds?: ReadonlySet<string>;
}): PageBlock[] | null {
  // High-level flow:
  // 1) resolve target + validate tree constraints
  // 2) apply exactly one operation (group, reorder, or move)
  // 3) cleanup and normalize happens upstream
  const context = buildOperationContext({
    event,
    blockMap,
    dropIndicator,
    fallbackOver,
    rootParentDirection,
    rootStackLayout,
  });
  if (!context) {
    return null;
  }

  if (
    !hasValidTargetHierarchy({
      context,
      childrenByParent,
      structuralRegionRootByBlockId,
      protectedBlockIds,
    })
  ) {
    return null;
  }

  const operation = resolveDropOperationForContext({ context, blocks });
  if (!operation) {
    return null;
  }

  if (operation.type === 'group') {
    return createDirectionalGroupIfNeeded({ context, blocks });
  }

  if (operation.type === 'insert') {
    const reorderedResult = reorderWithinSameParentIfNeeded({
      context,
      blocks,
      isParentDrop: isParentDropData,
    });
    if (reorderedResult) {
      return reorderedResult;
    }
  }

  return moveBlockToTargetParent({
    context,
    blocks,
    isParentDrop: isParentDropData,
    resolveMovedBlockColumnSpan,
  });
}
